import { FC } from "react";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { InnerBox } from "@/components/elements/InnerBox";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { Stack, Grid, Box } from "@mui/material";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { CompanyInfoItem } from "@/features/company/components/company-detail/CompanyInfoItem";

type TCompanyIndustryFieldDetailProps = {
  defaultValues?: Partial<TCompanyForm>;
};

export const CompanyIndustryFieldDetail: FC<TCompanyIndustryFieldDetailProps> = ({ defaultValues }) => {
  const { companySpecificIndustryOptions, getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry } =
    useGetCompanySpecificIndustryList();

  const getIndustryName = (id: number | string | null | undefined): string | undefined => {
    if (!id) return undefined;
    const found = companySpecificIndustryOptions.find((opt) => Number(opt.value) === Number(id));
    return found?.label;
  };

  const getClassificationNames = (industryId: number | null | undefined, ids: number[] | null | undefined): string | undefined => {
    if (!industryId || !ids || ids.length === 0) return undefined;
    const options = getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(industryId);
    const names = ids
      .map((id) => {
        const found = options.find((opt) => opt.value == id);
        return found?.label ?? "";
      })
      .filter(Boolean);
    return names.length > 0 ? names.join("、") : undefined;
  };

  const rows = [
    {
      index: 1,
      label: "①",
      industryId: defaultValues?.companySpecificIndustryId1,
      classificationIds: defaultValues?.companySpecificBusinessClassificationIds1,
    },
    {
      index: 2,
      label: "②",
      industryId: defaultValues?.companySpecificIndustryId2,
      classificationIds: defaultValues?.companySpecificBusinessClassificationIds2,
    },
    {
      index: 3,
      label: "③",
      industryId: defaultValues?.companySpecificIndustryId3,
      classificationIds: defaultValues?.companySpecificBusinessClassificationIds3,
    },
  ];

  const hasData = rows.some((row) => row.industryId || (row.classificationIds && row.classificationIds.length > 0));
  const hasMainIndustry = !!defaultValues?.mainIndustryName;

  return (
    <Stack gap={2}>
      <InnerBox title={<CompanyFormTitle text="業種・分野" />} showDivider={false}>
        {!hasData && !hasMainIndustry ? (
          <Box></Box>
        ) : (
          <Stack>
            <Grid container spacing={3}>
              <Grid item xs={6} mt={1}>
                <CompanyInfoItem label="業種" value={defaultValues?.mainIndustryName} />
              </Grid>
              <Grid item xs={6}></Grid>
            </Grid>
            {rows.map((row, index) => (
              <Box key={row.index}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <CompanyInfoItem label={`分野${row.label}`} value={getIndustryName(row.industryId)} isShowDivider={index !== rows.length - 1} />
                  </Grid>
                  <Grid item xs={6}>
                    <CompanyInfoItem
                      label="区分"
                      value={getClassificationNames(row.industryId, row.classificationIds)}
                      isShowDivider={index !== rows.length - 1}
                    />
                  </Grid>
                </Grid>
                {index < rows.length - 1 && <Box sx={{ my: 2 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </InnerBox>
    </Stack>
  );
};
